// Stripe Subscription interfaces matching the actual Stripe API response
export interface StripeSubscription {
    id: string;
    object: string;
    status: StripeSubscriptionStatus;
    created: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    canceledAt?: string;
    endedAt?: string;
    cancelAtPeriodEnd: boolean;
    items: StripeSubscriptionItem[];
    customer: string | StripeCustomer;
    metadata?: Record<string, string>;
    startDate: string;
}

export interface StripeSubscriptionItem {
    id: string;
    price?: StripePrice;
    plan?: StripePlan;
    quantity: number;
}

export interface StripePrice {
    id: string;
    unit_amount: number;
    currency: string;
    recurring: {
        interval: string;
        interval_count: number;
    };
    product: StripeProduct | null;
}

export interface StripePlan {
    id: string;
    amount: number;
    currency: string;
    interval: string;
    interval_count: number;
    product: StripeProduct | null;
    nickname?: string;
}

export interface StripeProduct {
    id: string;
    name: string;
    description?: string;
}

export interface StripeCustomer {
    id: string;
    name?: string;
    email?: string;
}

export enum StripeSubscriptionStatus {
    Active = 'active',
    Canceled = 'canceled',
    Incomplete = 'incomplete',
    IncompleteExpired = 'incomplete_expired',
    PastDue = 'past_due',
    Trialing = 'trialing',
    Unpaid = 'unpaid'
}

export class StripeSubscriptionModel {
    private subscription: StripeSubscription;

    constructor(data: StripeSubscription) {
        this.subscription = data;
    }

    get id(): string {
        return this.subscription?.id || '';
    }

    get status(): StripeSubscriptionStatus {
        return (this.subscription?.status as StripeSubscriptionStatus) || StripeSubscriptionStatus.Active;
    }

    get name(): string {
        if (!this.subscription?.items || this.subscription.items.length === 0) {
            return 'Subscription';
        }
        
        const firstItem = this.subscription.items[0];
        
        // Try plan first (older Stripe API)
        if (firstItem?.plan) {
            if (firstItem.plan.product?.name != null) {
                return firstItem.plan.product.name;
            }
            if (firstItem.plan.nickname) {
                return firstItem.plan.nickname;
            }
            if (firstItem.plan.id) {
                return `Subscription (${firstItem.plan.id})`;
            }
        }
        
        // Fallback to price (newer Stripe API)
        if (firstItem?.price) {
            if (firstItem.price.product?.name != null) {
                return firstItem.price.product.name;
            }
            if (firstItem.price.id) {
                return `Subscription (${firstItem.price.id})`;
            }
        }
        
        return 'Subscription';
    }

    get startDate(): Date {
        if (this.subscription?.startDate) {
            return new Date(this.subscription.startDate);
        }
        return new Date(); // Fallback to current date
    }

    get endDate(): Date | undefined {
        if (this.subscription?.endedAt) {
            return new Date(this.subscription.endedAt);
        }
        return undefined;
    }

    get monthlyAmount(): number {
        if (!this.subscription?.items || this.subscription.items.length === 0) {
            return 0;
        }
        
        const firstItem = this.subscription.items[0];
        
        // Try plan.amount first (preferred for older Stripe API responses)
        if (firstItem?.plan?.amount) {
            return firstItem.plan.amount / 100; // Convert from cents
        }
        
        // Fallback to price.unit_amount (newer Stripe API responses)
        if (firstItem?.price?.unit_amount) {
            return firstItem.price.unit_amount / 100; // Convert from cents
        }
        
        return 0;
    }

    get nextDueDate(): Date | undefined {
        if (this.subscription?.currentPeriodEnd) {
            return new Date(this.subscription.currentPeriodEnd);
        }
        return undefined;
    }

    get cancelAtPeriodEnd(): boolean {
        return this.subscription?.cancelAtPeriodEnd || false;
    }

    get canceledAt(): Date | undefined {
        if (this.subscription?.canceledAt) {
            return new Date(this.subscription.canceledAt);
        }
        return undefined;
    }

    get created(): Date {
        if (this.subscription?.created) {
            return new Date(this.subscription.created);
        }
        return new Date(); // Fallback to current date
    }

    get isActive(): boolean {
        return this.status === StripeSubscriptionStatus.Active;
    }

    get isInactive(): boolean {
        return [
            StripeSubscriptionStatus.Canceled,
            StripeSubscriptionStatus.Incomplete,
            StripeSubscriptionStatus.IncompleteExpired,
            StripeSubscriptionStatus.Unpaid,
            StripeSubscriptionStatus.PastDue
        ].includes(this.status);
    }

    get statusDisplayName(): string {
        switch (this.status) {
            case StripeSubscriptionStatus.Active:
                return 'Active';
            case StripeSubscriptionStatus.Canceled:
                return 'Canceled';
            case StripeSubscriptionStatus.Incomplete:
                return 'Incomplete';
            case StripeSubscriptionStatus.IncompleteExpired:
                return 'Incomplete Expired';
            case StripeSubscriptionStatus.PastDue:
                return 'Past Due';
            case StripeSubscriptionStatus.Trialing:
                return 'Trialing';
            case StripeSubscriptionStatus.Unpaid:
                return 'Unpaid';
            default:
                return this.status;
        }
    }

    get formattedMonthlyAmount(): string {
        return `$${this.monthlyAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
    }
}